import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CheerCard from './CheerCard';
import { PURPOSE_KINDS, PurposeChips, purposeCountKey } from './EventTags';
import { apiErrorText, apiRequest } from '../../utils';
import '../../pages/event/MemberHome.css';
import '../../pages/event/CompanyEvents.css';
import '../../pages/event/SendCheer.css';

/* Is anybody cheering anybody here — the admin's read of recognition. F7.

   ★ **Everything on this screen is already public inside the company.** A cheer becomes visible
   when the person it is about chooses to show it, and from that moment it is on the company feed
   and on their profile. So this tab discloses nothing new; it gathers what is scattered, which is
   the only reason an admin needs a screen for it at all. A pending cheer never reaches here — the
   store filters on accepted, not the caller.

   ★ **No ranking, and that is a decision rather than an omission.** Three numbers say how many
   people take part; who is cheered MOST stays unbuilt, on the owner's standing call that a
   visible personal ranking is a culture decision. The chips are the honest version of the
   question: they say which of the company's values are alive without naming anybody.

   ★ The tally comes FIRST and the sentences after it. Three cheers all naming "Win Together" is a
   fact about a company that a scroll of paragraphs never states — and the paragraphs are what
   make it true rather than decorative, which is why both are here.

   ★ **The tally is the way in.** The chips are the calendar's own filter chips (`PurposeChips`,
   grouped by kind and counted inside the pill), so clicking one answers the question the number
   raises: nineteen cheers named this — what did people actually say? Filtered on the SERVER,
   because the list of sentences is capped at twenty-five and sifting the page in the browser
   would show four cards under a chip that says nineteen.

   ★ The chips never move while a filter is on. `tally` and `totals` come back unfiltered by
   design: they are the map, and a map that redraws itself around the pin cannot be clicked
   again. */

const NO_PICKS = { theme: [], value: [], sdg: [] };

const key = (v) => String(v || '').toLowerCase();

/* The chips as the picker speaks them: a catalogue per kind, and a count per chip. Built out of
   the tally itself rather than out of the company's whole catalogue — a value nobody has ever
   been cheered for is a chip that finds nothing, and this row is a report before it is a
   filter. The tally arrives commonest first, so each kind's row stays in that order. */
const asCatalog = (tally) => {
  const catalog = { theme: [], value: [], sdg: [] };
  const counts = {};
  (tally || []).forEach(({ chip, n }) => {
    if (!catalog[chip.kind]) return;
    catalog[chip.kind].push(chip);
    counts[purposeCountKey(chip.kind, chip.id)] = n;
  });
  return { catalog, counts };
};

const CheerStats = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [picks, setPicks] = useState(NO_PICKS);
  const [busy, setBusy] = useState(false);

  const on = PURPOSE_KINDS.flatMap((kind) => (picks[kind] || []).map((id) => `${kind}:${id}`));
  const filtering = on.length > 0;
  /* A string rather than the object in the dependency list: `picks` is a fresh object on every
     toggle, and half of them (a chip in a kind nobody has picked) leave the query identical. */
  const query = on.join(',');

  useEffect(() => {
    setBusy(true);
    apiRequest('GET', `/company/recognition-tally${query ? `?for=${encodeURIComponent(query)}` : ''}`)
      .then((body) => { setData(body); setError(null); })
      .catch((err) => setError(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  }, [t, query]);

  if (data === null) {
    return error
      ? <p className="bravos-cal__notice">{error}</p>
      : <span className="auth__spinner" />;
  }

  const totals = data.totals || {};
  const recent = data.recent || [];
  const { catalog, counts } = asCatalog(data.tally);

  /* The caller owns the list, as everywhere these chips are used. Lower-cased on the way in
     because ids arrive lower-cased from the API but a chip's own id need not be. */
  const toggle = (kind, id) => setPicks((prev) => {
    const chosen = new Set((prev[kind] || []).map(key));
    if (chosen.has(key(id))) chosen.delete(key(id)); else chosen.add(key(id));
    return { ...prev, [kind]: [...chosen] };
  });

  /* Nothing SHOWN is not the same as nothing sent, and the empty state says so: cheers may well
     be sitting unanswered, and an admin who reads "nobody cheers here" would be wrong. */
  if (!totals.shown) {
    return <p className="ce-note">{t('cheerStatsNone')}</p>;
  }

  return (
    <>
      <div className="ce-row__top">
        <span className="ce-pill ce-pill--going">{t('cheerStatShown', { n: totals.shown })}</span>
        <span className="ce-pill ce-pill--draft">{t('cheerStatGivers', { n: totals.givers })}</span>
        <span className="ce-pill ce-pill--draft">
          {t('cheerStatReceivers', { n: totals.receivers })}
        </span>
      </div>

      {(data.tally || []).length > 0 && (
        <>
          <p className="bravos-form__label mt-3">{t('cheerStatsWhatFor')}</p>
          <p className="ce-note">{t('cheerStatsWhatForNote')}</p>
          <PurposeChips catalog={catalog} picks={picks} onToggle={toggle} counts={counts} />
          {filtering && (
            <button type="button" className="bravos-tag__add mt-2"
                    onClick={() => setPicks(NO_PICKS)}>
              {t('clearFilter')}
            </button>
          )}
        </>
      )}

      <p className="bravos-form__label mt-3">
        {filtering ? t('cheerStatsForThese') : t('cheerStatsRecent')}
      </p>
      {/* Dimmed rather than replaced by a spinner while the next set arrives: the chips above are
          what somebody is looking at, and a list that vanishes under the click reads as a reload
          of the whole screen. */}
      <div className={busy ? 'sc-cheers is-busy' : 'sc-cheers'}>
        {recent.length === 0 && <p className="ce-note">{t('cheerStatsNoneForFilter')}</p>}
        {recent.map((cheer) => <CheerCard key={cheer.id} cheer={cheer} />)}
        {/* Only when the cap is what ended the list -- otherwise the chip's count and the cards
            under it are the same set and saying so would be noise. */}
        {filtering && data.limit && recent.length >= data.limit && (
          <p className="ce-note mt-2">{t('cheerStatsCapped', { n: data.limit })}</p>
        )}
      </div>
    </>
  );
};

export default CheerStats;
